import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "@lyder-talk/core/lib/db-client";
import { queryKeys } from "@/lib/query-keys";
import type { Json } from "@/integrations/supabase/types";
import { getCurrentUser } from "@lyder-talk/core";
import { fetchUserDataBatch } from "@/lib/user-data-helpers";
export interface HistorySession {
  id: string;
  topic: string;
  slug: string;
  questions?: Json;
  vocabulary?: Json;
  speaking_guide?: Json;
  user_id: string;
  created_at: string;
  is_public?: boolean;
  is_pinned?: boolean;
  category?: string | null;
  banner_url?: string | null;
  creator_email?: string;
  creator_avatar_url?: string | null;
  creator_display_name?: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  bookmark_count?: number;
}

interface UsePracticeSessionsOptions {
  initialLimit?: number;
  /**
   * Skip the query entirely. The sidebar collapses these lists by default, so
   * it passes its per-group open state here instead of fetching 30 sessions
   * (plus their counts and creator profiles) for a list nobody expanded.
   */
  enabled?: boolean;
}

const fetchPracticeSessions = async (userId: string, limit?: number): Promise<HistorySession[]> => {
  let query = db.from("practice_sessions")
    .select(`
      id,
      topic,
      slug,
      banner_url,
      created_at,
      user_id,
      is_public,
      is_pinned,
      category_id,
      view_count,
      like_count,
      comment_count,
      bookmark_count,
      questions (id),
      vocabulary (id),
      categories!category_id (
        id,
        name
      )
    `)
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) throw error;

  const sessions = (data || []).map((session) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const typedSession = session as any;
    // List views only need question/vocabulary COUNTS (rendered as "N questions").
    // `questions(id)` / `vocabulary(id)` embed the child keys only, and we expand
    // them to length-only placeholder arrays so downstream `.length` keeps
    // working without fetching (and parsing) the full content. The `rel(count)`
    // form PostgREST answered is not supported by the table gateway, which is why
    // this asks for ids instead. Detail pages fetch the real content through
    // their own queries.
    return {
      ...typedSession,
      category: typedSession?.categories?.name || null,
      banner_url: typedSession?.banner_url || null,
      questions: Array.from({ length: typedSession.questions?.length ?? 0 }),
      vocabulary: Array.from({ length: typedSession.vocabulary?.length ?? 0 }),
    };
  });

  // Fetch user data for creator info (user's own sessions, so fetch current user's data)
  const userIds = [...new Set(sessions.map(s => s.user_id).filter(Boolean))];
  const userDataMap = await fetchUserDataBatch(userIds);

  return sessions.map((session) => {
    const userData = userDataMap.get(session.user_id);
    return {
      ...session,
      creator_email: userData?.email || "Unknown",
      creator_avatar_url: userData?.avatar_url || null,
      creator_display_name: userData?.display_name || "Unknown",
    };
  });
};

const INITIAL_LIMIT = 30;
const LOAD_MORE_PAGE_SIZE = 9;

export const usePracticeSessions = (userId: string | null, options?: UsePracticeSessionsOptions) => {
  const queryClient = useQueryClient();
  const initialLimit = options?.initialLimit ?? INITIAL_LIMIT;
  const [loadedCount, setLoadedCount] = useState(initialLimit);

  const { data: history = [], isLoading: loading, isFetching } = useQuery({
    queryKey: queryKeys.practiceSessions.list(userId, loadedCount),
    queryFn: () => fetchPracticeSessions(userId!, loadedCount),
    enabled: (options?.enabled ?? true) && !!userId,
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
  });

  const pinnedSessions = useMemo(() => {
    return history
      .filter((session) => session.is_pinned)
      .map((session) => session.id);
  }, [history]);

  const hasMore = history.length === loadedCount;
  const isFetchingMore = isFetching && history.length > 0;

  const loadMore = useCallback((pageSize = LOAD_MORE_PAGE_SIZE) => {
    setLoadedCount(prev => prev + pageSize);
  }, []);

  const loadHistory = useCallback(async (userIdParam?: string) => {
    const targetUserId = userIdParam || userId;
    if (!targetUserId) return;

    setLoadedCount(initialLimit);
    await queryClient.invalidateQueries({ queryKey: queryKeys.practiceSessions.byUser(targetUserId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.practiceHistory.byUser(targetUserId) });
  }, [userId, queryClient, initialLimit]);

  const togglePin = async (sessionId: string): Promise<{ success: boolean; isPinned?: boolean }> => {
    try {
      const user = await getCurrentUser();

      const session = history.find((s) => s.id === sessionId);
      let isPinned = session?.is_pinned;

      if (isPinned === undefined) {
         const { data, error } = await db.from("practice_sessions")
           .select("is_pinned")
           .eq("id", sessionId)
           .single();

         if (!error && data) {
           isPinned = data.is_pinned;
         }
      }

      const newPinState = !isPinned;

      const { error } = await db.from("practice_sessions")
        .update({ is_pinned: newPinState } as { is_pinned: boolean })
        .eq("id", sessionId)
        .eq("user_id", user.id);

      if (error) throw error;

      queryClient.setQueriesData<HistorySession[]>(
        { queryKey: queryKeys.practiceSessions.byUser(user.id) },
        (oldData) => oldData?.map((s) =>
          s.id === sessionId ? { ...s, is_pinned: newPinState } : s
        ) || []
      );

      await queryClient.invalidateQueries({ queryKey: queryKeys.practiceHistory.byUser(user.id) });

      return { success: true, isPinned: newPinState };
    } catch (error) {
      console.error("Error toggling pin:", error);
      return { success: false };
    }
  };

  const deleteSession = async (id: string) => {
    try {
      const user = await getCurrentUser();

      const { error } = await db.from("practice_sessions")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: queryKeys.practiceSessions.byUser(user.id) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.practiceHistory.byUser(user.id) });
      return { success: true };
    } catch (error) {
      console.error("Error deleting session:", error);
      return { success: false, error };
    }
  };

  return {
    history,
    loading,
    pinnedSessions,
    loadHistory,
    togglePin,
    deleteSession,
    loadMore,
    hasMore,
    isFetchingMore,
  };
};
